import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from '@mui/material'
import {
  DescriptionOutlined as DocIcon,
  Close as CloseIcon,
  Language as WebIcon,
  Warning as WarningIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material'
import { smbsApi, SMBData } from '../../services/api'
import { MAX_DOCUMENT_SIZE_LABEL } from '../../constants/uploads'
import { useNotification } from '../../contexts/NotificationContext'
import { validatePdfFile, getFileNameFromPath } from '../../utils/uploads'

interface UpdateKnowledgeDialogProps {
  open: boolean
  onClose: () => void
  smb: SMBData | Partial<SMBData>
  onSuccess: (smb: SMBData, scrapingPromise?: Promise<any>, isDocument?: boolean) => void
  mode?: 'setup' | 'update'
  onSkip?: () => void
  dialogSx?: object
}

const UpdateKnowledgeDialog: React.FC<UpdateKnowledgeDialogProps> = ({
  open,
  onClose,
  smb,
  onSuccess,
  mode = 'update',
  onSkip,
  dialogSx = {},
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [formData, setFormData] = useState({ website: '' })
  const [documentFile, setDocumentFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [nudgeOpen, setNudgeOpen] = useState(false)
  const { showNotification } = useNotification()

  useEffect(() => {
    if (smb) setFormData({ website: smb.website || '' })
  }, [smb])

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }))
    if (error) setError(null)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const validationError = validatePdfFile(file)
    if (validationError) {
      setError(validationError)
      event.target.value = ''
      return
    }
    setDocumentFile(file)
    setError(null)
  }

  const confirmUpdate = (deleteData: boolean) => {
    const isRemoving = (String(formData.website) || '').trim().length === 0
    const payload: any = { 
      ...formData, 
      smb_id: smb.id, 
      delete_scraped_data: deleteData,
      clear_website: isRemoving 
    }
    const updatePromise = documentFile
      ? smbsApi.createUpdateSMB(payload, { file: documentFile })
      : smbsApi.createUpdateSMB(payload)
    if (documentFile) showNotification('Uploading business document…', 'info')
    const optimisticSMB = { ...smb, ...formData, website: isRemoving ? '' : formData.website }
    onSuccess(optimisticSMB as SMBData, updatePromise, !!documentFile)
    setNudgeOpen(false)
    onClose()
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (mode === 'setup') { confirmUpdate(false); return }
    const hadWebsite = (smb.website || '').trim().length > 0
    const isRemoving = (String(formData.website) || '').trim().length === 0
    if (hadWebsite && isRemoving) { setNudgeOpen(true); return }
    confirmUpdate(false)
  }

  const handleClose = () => {
    if (mode === 'setup' && onSkip) { onSkip(); return }
    setError(null)
    onClose()
  }

  const hasWebsite = formData.website.trim().length > 0
  const hasChanges = formData.website !== (smb.website || '') || documentFile !== null
  const isSubmitDisabled = mode === 'setup' ? (!hasWebsite && !documentFile) : !hasChanges

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={dialogSx}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(15, 23, 42, 0.4)',
              backdropFilter: mode === 'setup' ? 'none' : 'none',
            }
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18)',
            ...(isMobile && {
              display: 'flex',
              flexDirection: 'column',
            }),
          },
        }}
      >
        {/* ── Premium header with gradient ── */}
        <Box
          sx={{
            background: 'linear-gradient(90deg, #3F66BB 0%, #34A1FB 100%)',
            px: 3,
            pt: 3,
            pb: 2.5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative orb */}
          <Box sx={{ position: 'absolute', top: '-50%', left: '-10%', width: '50%', height: '200%', background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, rgba(0,0,0,0) 70%)', transform: 'rotate(30deg)', pointerEvents: 'none' }} />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Icon badge */}
              <Box sx={{
                width: 40, height: 40, borderRadius: '12px',
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(8px)',
              }}>
                <SparkleIcon sx={{ fontSize: 20, color: '#fff' }} />
              </Box>
              <Box>
                <Typography
                  sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}
                >
                  {mode === 'setup' ? 'Set Up' : 'Edit'} Business Knowledge
                </Typography>
                <Typography
                  sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.78)', mt: 0.25, fontWeight: 400 }}
                >
                  Help your AI agent understand your business
                </Typography>
              </Box>
            </Box>

            {mode === 'update' && (
              <IconButton
                onClick={handleClose}
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' },
                  mt: -0.5, mr: -0.5,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 auto', overflowY: 'auto' }}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            )}

            {/* ── Document section ── */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
                  Business Document (Optional)
                </Typography>
              </Box>

              {documentFile ? (
                /* File selected — polished chip card */
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 1.5,
                  border: '1px solid #C7D7FD',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #EEF2FF 0%, #E0F2FE 100%)',
                }}>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: '10px',
                    background: 'linear-gradient(135deg, #3F66BB 0%, #34A1FB 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <DocIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1e293b',
                    }}>
                      {documentFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {(documentFile.size / 1024 / 1024).toFixed(2)} MB · PDF
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setDocumentFile(null)}
                    sx={{
                      flexShrink: 0,
                      color: '#94a3b8',
                      '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' },
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) : smb.document_name || smb.document_original_path ? (
                /* Existing document — polished chip card */
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 1.5,
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  bgcolor: '#F8FAFC',
                }}>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: '10px',
                    bgcolor: '#E2E8F0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <DocIcon sx={{ fontSize: 18, color: '#64748b' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1e293b',
                    }}>
                      {smb.document_name || getFileNameFromPath(smb.document_original_path)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Existing Document
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    component="label"
                    sx={{
                      flexShrink: 0,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      py: 0.5, px: 1.5,
                      borderColor: '#CBD5E1',
                      color: '#475569',
                      '&:hover': { bgcolor: '#F1F5F9', borderColor: '#94A3B8' }
                    }}
                  >
                    Replace
                    <input type="file" hidden accept=".pdf,application/pdf" onChange={handleFileChange} />
                  </Button>
                </Box>
              ) : (
                /* Empty — dashed drop zone */
                <Box
                  component="label"
                  sx={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: 0.75, py: 3,
                    border: '1.5px dashed #CBD5E1',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.18s ease',
                    '&:hover': {
                      borderColor: '#3F66BB',
                      background: 'rgba(63,102,187,0.04)',
                    },
                  }}
                >
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    bgcolor: '#F1F5F9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.25,
                  }}>
                    <DocIcon sx={{ fontSize: 22, color: '#94a3b8' }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                    Upload Business Document
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    PDF · Max {MAX_DOCUMENT_SIZE_LABEL} · Optional
                  </Typography>
                  <input type="file" hidden accept=".pdf,application/pdf" onChange={handleFileChange} />
                </Box>
              )}
            </Box>

            {/* ── Website section ── */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
                  Website (Optional)
                </Typography>
              </Box>
              <TextField
                fullWidth
                value={formData.website}
                onChange={handleChange('website')}
                placeholder="https://www.yourbusiness.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WebIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '12px', fontSize: '0.9rem' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': { borderColor: '#3F66BB' },
                    '&.Mui-focused fieldset': { borderColor: '#3F66BB' },
                  },
                }}
              />
            </Box>
          </DialogContent>

          {/* ── Footer actions ── */}
          <DialogActions sx={{
            px: 3, py: 2.5,
            borderTop: '1px solid #F1F5F9',
            gap: 1.5,
            ...(isMobile && {
              position: 'sticky',
              bottom: 0,
              bgcolor: '#fff',
              justifyContent: 'space-between',
              flexShrink: 0,
              pb: 4, // extra bottom padding for safe area
            })
          }}>
            {mode === 'setup' ? (
              <>
                <Button
                  onClick={onSkip}
                  sx={{
                    color: '#34A1FB', fontWeight: 600,
                    '&:hover': { bgcolor: '#F0F8FF', color: '#3F66BB' },
                    borderRadius: '10px',
                    ...(isMobile && { flex: 1 }),
                  }}
                >
                  Skip
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitDisabled}
                  sx={{
                    ml: isMobile ? 0 : 'auto',
                    borderRadius: '10px',
                    px: 3,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #3F66BB 0%, #34A1FB 100%)',
                    boxShadow: '0 4px 12px rgba(63,102,187,0.35)',
                    '&:hover': { boxShadow: '0 6px 16px rgba(63,102,187,0.45)' },
                    '&:disabled': { background: '#E2E8F0', boxShadow: 'none', color: '#94a3b8' },
                    ...(isMobile && { flex: 1 }),
                  }}
                >
                  Finish Setup
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleClose}
                  sx={{
                    color: '#64748b', fontWeight: 500,
                    '&:hover': { bgcolor: '#F8FAFC' },
                    borderRadius: '10px',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitDisabled}
                  sx={{
                    ml: 'auto',
                    borderRadius: '10px',
                    px: 3,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #3F66BB 0%, #34A1FB 100%)',
                    boxShadow: '0 4px 12px rgba(63,102,187,0.35)',
                    '&:hover': { boxShadow: '0 6px 16px rgba(63,102,187,0.45)' },
                    '&:disabled': { background: '#E2E8F0', boxShadow: 'none', color: '#94a3b8' },
                  }}
                >
                  Save Changes
                </Button>
              </>
            )}
          </DialogActions>
        </form>

        {/* ── Nudge: remove website confirmation ── */}
        <Dialog open={nudgeOpen} onClose={() => setNudgeOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
          <Box sx={{ px: 3, pt: 3, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '10px', bgcolor: '#FEF3C7',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <WarningIcon sx={{ fontSize: 20, color: '#D97706' }} />
              </Box>
              <Typography fontWeight={700} sx={{ fontSize: '1rem', color: '#1e293b' }}>
                Remove scraped data?
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
              You're removing <strong style={{ color: '#1e293b' }}>{smb.website}</strong>. We still have
              scraped content stored from this site. Would you like to delete it too?
            </Typography>
          </Box>
          <DialogActions sx={{ px: 3, py: 2.5, flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained" color="error" fullWidth
              onClick={() => confirmUpdate(true)}
              sx={{ borderRadius: '10px', fontWeight: 600 }}
            >
              Remove website & delete data
            </Button>
            <Button
              variant="outlined" fullWidth
              onClick={() => confirmUpdate(false)}
              sx={{ borderRadius: '10px', fontWeight: 500, borderColor: '#E2E8F0', color: '#475569' }}
            >
              Remove website only (keep data)
            </Button>
            <Button variant="text" onClick={() => setNudgeOpen(false)}
              sx={{ color: '#94a3b8', fontWeight: 500, borderRadius: '10px' }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </>
  )
}

export default UpdateKnowledgeDialog
